import { forwardRef, useCallback, useImperativeHandle, useRef, type ReactNode } from 'react'
import { PanelGroup, type ImperativePanelGroupHandle } from 'react-resizable-panels'
import { ResizableHandle, ResizablePanel } from '@/components/ui/resizable'
import { cn } from '@/lib/utils'
import { snapLayout } from '@/lib/templatePreview'

export type SplitViewProps = {
  left: ReactNode
  right: ReactNode
  direction?: 'horizontal' | 'vertical'
  className?: string
  // Story 6.4 — notifié (uniquement sur changement) quand le panneau droit est replié à 0 %.
  onRightCollapsedChange?: (collapsed: boolean) => void
}

// Story 6.4 — handle impératif : rouvrir le panneau droit replié (50/50).
export type SplitViewHandle = {
  expandRight: () => void
}

// Mise en page deux panneaux redimensionnables (chat | artefact), story 6.3.
// Ne connaît rien des templates : en fin de drag, le ratio s'aimante sur 0/40/50/60/100.
export const SplitView = forwardRef<SplitViewHandle, SplitViewProps>(function SplitView(
  { left, right, direction = 'horizontal', className, onRightCollapsedChange },
  ref
) {
  const groupRef = useRef<ImperativePanelGroupHandle>(null)
  const rightCollapsedRef = useRef(false)

  useImperativeHandle(
    ref,
    () => ({
      expandRight: () => groupRef.current?.setLayout([50, 50]),
    }),
    []
  )

  const handleDragging = useCallback((dragging: boolean) => {
    if (dragging) return
    const group = groupRef.current
    if (group) group.setLayout(snapLayout(group.getLayout()))
  }, [])

  const handleLayout = useCallback(
    (sizes: number[]) => {
      const collapsed = sizes[1] === 0
      if (collapsed === rightCollapsedRef.current) return
      rightCollapsedRef.current = collapsed
      onRightCollapsedChange?.(collapsed)
    },
    [onRightCollapsedChange]
  )

  // PanelGroup direct : le wrapper shadcn est une fonction simple (React 18) qui avale la ref.
  return (
    <PanelGroup
      ref={groupRef}
      direction={direction}
      onLayout={handleLayout}
      data-slot="resizable-panel-group"
      className={cn('flex h-full w-full data-[panel-group-direction=vertical]:flex-col', className)}
    >
      <ResizablePanel id="chat" order={1} defaultSize={50} minSize={20} collapsible collapsedSize={0}>
        <div className="flex h-full min-h-0 flex-col">{left}</div>
      </ResizablePanel>
      <ResizableHandle
        withHandle
        aria-label="Redimensionner le chat et la prévisualisation"
        onDragging={handleDragging}
      />
      <ResizablePanel id="preview" order={2} defaultSize={50} minSize={20} collapsible collapsedSize={0}>
        <div className="flex h-full min-h-0 flex-col">{right}</div>
      </ResizablePanel>
    </PanelGroup>
  )
})
